/**
 * Copyright © 2026 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import {
  AccountManagementProvider,
  BalanceCard,
  ViewLatestStatementCard,
} from "@farmlands/account-management";
import { FlexPageContentLayout } from "@farmlands/ui-library";
import _ from "lodash";
import { useEffect } from "react";
import { useSelector } from "react-redux";

import { PageContainer } from "@/components/layout/PageContainer";
import { usePermission } from "@/hooks/usePermission";
import { DashboardPromotionsAtomic } from "@/services/atomic/DashboardPromotionsAtomic";
import { useAppDispatch } from "@/store/rootStore";
import { TestIds } from "@/testIds";

import { useAccountManagementContextValue } from "../account/hooks/useAccountManagementContextValue";
import { selectCurrentFarmerAccount, selectFirstName } from "../azure-b2c/storage";
import { getSalesAgreements, selectAllSalesAgreements } from "../sales-agreements/storage";

import { ActiveSalesAgreementsSection } from "./components/ActiveSalesAgreementsSection";
import { GetInTouchCard } from "./components/GetInTouchCard";
import { QuickLinksSection } from "./components/QuickLinksSection";
import SearchBar from "./components/SearchBar";

const Home = () => {
  const dispatch = useAppDispatch();
  const username = useSelector(selectFirstName);
  const account = useSelector(selectCurrentFarmerAccount);
  const { contextValue, isLoading, error } = useAccountManagementContextValue();

  const canViewShop = usePermission("FarmlandsProShop_ShopView").allowed;
  const canViewStatements = usePermission("Statements_StatementsViewAndDownload").allowed;
  const canViewBalanceCreditLimit = usePermission("BalanceAndCredit_CreditLimitView").allowed;
  const canViewBalanceAndCredit = usePermission("BalanceAndCredit_BalanceView").allowed;
  const canViewSalesAgreements = usePermission("SalesAgreements_AgreementView").allowed;
  const canViewBalanceCard = canViewBalanceCreditLimit && canViewBalanceAndCredit;
  const showAccountManagementCards = canViewStatements || canViewBalanceCard;
  const hasAccountContext = !isLoading && !error && contextValue.account.selectedAccount;
  const salesAgreements = useSelector(selectAllSalesAgreements);

  useEffect(() => {
    if (canViewSalesAgreements && account?.accountNumber) {
      dispatch(getSalesAgreements(account.accountNumber));
    }
  }, [account?.accountNumber, canViewSalesAgreements, dispatch]);

  const showViewLatestStatementCard =
    showAccountManagementCards && hasAccountContext && canViewStatements;
  const showBalanceCard = showAccountManagementCards && hasAccountContext && canViewBalanceCard;
  const showActiveSalesAgreementsSection = canViewSalesAgreements && !_.isEmpty(salesAgreements);
  const isMainContentEmpty =
    !showViewLatestStatementCard && !showBalanceCard && !showActiveSalesAgreementsSection;

  const headerContent = (
    <div className="flex flex-col gap-6" data-testid={TestIds.feat.home.headerContent}>
      <h1 className="text-3xl font-extrabold text-forest-green-950">Kia Ora, {username}</h1>
      {canViewShop && <SearchBar />}
      <DashboardPromotionsAtomic />
    </div>
  );

  const mainContent = (
    <div className="flex flex-col gap-8" data-testid={TestIds.feat.home.mainContent}>
      {(showViewLatestStatementCard || showBalanceCard) && (
        <AccountManagementProvider value={contextValue}>
          {showViewLatestStatementCard && <ViewLatestStatementCard />}
          {showBalanceCard && (
            <BalanceCard
              labelsColourClass="text-center text-verdure-white"
              digitsColourClass="text-accent"
              backgroundColourClass="bg-forest-green-950"
            />
          )}
        </AccountManagementProvider>
      )}
      {showActiveSalesAgreementsSection && <ActiveSalesAgreementsSection />}
    </div>
  );

  const secondaryContent = (
    <div
      className="flex flex-wrap items-start gap-8"
      data-testid={TestIds.feat.home.secondaryContent}
    >
      <div
        className="min-w-0 flex-1 basis-[16rem]"
        data-testid={TestIds.feat.home.secondaryContentItem}
      >
        <QuickLinksSection />
      </div>
      <div
        className="min-w-0 flex-1 basis-[16rem]"
        data-testid={TestIds.feat.home.secondaryContentItem}
      >
        <GetInTouchCard />
      </div>
    </div>
  );

  return (
    <PageContainer data-testid={TestIds.pages.home}>
      <FlexPageContentLayout
        headerContent={headerContent}
        mainContent={mainContent}
        secondaryContent={secondaryContent}
        className="max-w-none p-0 lg:p-0"
        classNames={{ main: isMainContentEmpty ? "hidden" : "" }}
      />
    </PageContainer>
  );
};

export default Home;
